import { liveHours, type EmployeeSnapshot } from '@klukka/client';
import {
  calcPeriod,
  daysFor,
  expectedWork,
  orlofStatus,
  payoutDate,
  periodInfo,
  periodKey,
  type OrlofStatus,
  type PeriodCalc,
  type PeriodInfo,
} from '@klukka/core';

/**
 * Everything the phone screens read, derived once per render from the snapshot.
 *
 * `now` is always the current period with the live clock folded in — the Home
 * tab never leaves it. `sel` follows the ‹ › period navigation and is a closed,
 * historical view when the offset is negative.
 */
export interface EmployeeView {
  live: number;
  running: boolean;
  now: { pinfo: PeriodInfo; calc: PeriodCalc; adjSum: number };
  sel: { pinfo: PeriodInfo; calc: PeriodCalc; adjSum: number; key: string };
  todayRow: PeriodCalc['rows'][number] | undefined;
  workedHours: number;
  expected: { days: number; hours: number };
  sickDays: number;
  payout: { now: string; sel: string };
  /**
   * The vacation account as it stood at the end of the selected period —
   * opening balance carried in, plus everything accrued since, less what has
   * been taken. Stepping back a period gives that period's statement, not
   * today's with a month lopped off.
   */
  orlof: OrlofStatus;
  /** Dates the employee has edited themselves — marked ✎ in the day list. */
  editedDates: Set<string>;
}

export function deriveEmployeeView(snap: EmployeeSnapshot, offset: number): EmployeeView {
  const { company, employee, shift, serverToday } = snap;
  const settings = company.settings;
  const live = liveHours(shift);

  const pinfoNow = periodInfo(settings, serverToday, 0);
  const pinfoSel = offset === 0 ? pinfoNow : periodInfo(settings, serverToday, offset);

  const inRange = (from: string, to: string) => (d: string) => d >= from && d <= to;
  const sumAdj = (p: PeriodInfo) =>
    snap.adjustments.filter((a) => inRange(p.start, p.end)(a.date)).reduce((s, a) => s + a.amount, 0);

  const adjNow = sumAdj(pinfoNow);
  const adjSel = offset === 0 ? adjNow : sumAdj(pinfoSel);

  const daysNow = daysFor(employee, settings, serverToday, live, 0);
  const calcNow = calcPeriod(employee, daysNow, settings, adjNow);
  const daysSel = offset === 0 ? daysNow : daysFor(employee, settings, serverToday, 0, offset);
  const calcSel = offset === 0 ? calcNow : calcPeriod(employee, daysSel, settings, adjSel);

  const workedHours = calcSel.hours.dvH + calcSel.hours.yv1H + calcSel.hours.yv2H;

  return {
    live,
    running: !!shift && !shift.clockOutAt,
    now: { pinfo: pinfoNow, calc: calcNow, adjSum: adjNow },
    sel: { pinfo: pinfoSel, calc: calcSel, adjSum: adjSel, key: periodKey(pinfoSel) },
    todayRow: calcNow.rows[calcNow.rows.length - 1],
    workedHours,
    expected: expectedWork(pinfoSel, settings, employee.expectedHoursOverride),
    sickDays: calcSel.rows.filter((r) => r.sick).length,
    payout: { now: payoutDate(pinfoNow), sel: payoutDate(pinfoSel) },
    orlof: orlofStatus({
      emp: employee,
      settings,
      entries: snap.orlof,
      // Statement date: the end of a closed period, today for the open one.
      asOf: offset === 0 ? serverToday : pinfoSel.end,
    }),
    editedDates: new Set(snap.edits.map((e) => e.date)),
  };
}

/** Total hours across all three rate classes. */
export function totalHours(calc: PeriodCalc): number {
  return calc.hours.dvH + calc.hours.yv1H + calc.hours.yv2H;
}
